'use client';

import { useState } from 'react';
import { CVEData } from '../utils/nvdApi';
import { KEVEntry } from '../utils/cisaKev';
import Link from 'next/link';
import { ArrowLeft, AlertTriangle, Shield, Link2, Code, TrendingUp } from 'lucide-react';
import { getSeverityColor } from '../utils/nvdApi';
import { BreadcrumbItem } from '@/components/Breadcrumbs';
import KEVBadge from '../components/KEVBadge';
import EPSSBadge from '../components/EPSSBadge';
import RiskMatrix from '../components/RiskMatrix';
import CVEHero from './CVEHero';
import SeverityExplainer from './SeverityExplainer';
import RelatedResources from './RelatedResources';
import RelatedCVEs from './RelatedCVEs';

interface CVESummary {
  id: string;
  description: string;
  cvss_v3_score: number | null;
  cvss_v3_severity: string | null;
  published: string;
}

interface CVEDetailClientProps {
  cve: CVEData;
  kevEntry?: KEVEntry | null;
  breadcrumbs?: BreadcrumbItem[];
  relatedByCWE?: CVESummary[];
  relatedBySeverity?: CVESummary[];
}

type Tab = 'overview' | 'cvss' | 'epss' | 'references' | 'remediation';

// Check if a CWE ID is valid and linkable (not a placeholder like NVD-CWE-noinfo)
function isValidCWEId(cweId: string): boolean {
  return /^CWE-\d+$/i.test(cweId);
}

export default function CVEDetailClient({ cve, kevEntry, breadcrumbs, relatedByCWE = [], relatedBySeverity = [] }: CVEDetailClientProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const tabs: { id: Tab; label: string; icon: React.ReactNode; show?: boolean }[] = [
    { id: 'overview', label: 'Overview', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'cvss', label: 'CVSS Details', icon: <Shield className="w-4 h-4" /> },
    { id: 'epss', label: 'EPSS & Risk', icon: <TrendingUp className="w-4 h-4" />, show: !!cve.epss },
    { id: 'references', label: 'References', icon: <Link2 className="w-4 h-4" /> },
    { id: 'remediation', label: 'Remediation', icon: <Code className="w-4 h-4" /> },
  ];

  const visibleTabs = tabs.filter(tab => tab.show !== false);

  const score = cve.cvssV3?.baseScore ?? cve.cvssV2?.baseScore;
  const severity = cve.cvssV3?.baseSeverity;
  const vectorString = cve.cvssV3?.vectorString;

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Hero Section */}
      <CVEHero cve={cve} kevEntry={kevEntry} breadcrumbs={breadcrumbs} />

      {/* Back Button */}
      <Link
        href="/tools/security/cve-lookup"
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to CVE Lookup
      </Link>

      {/* KEV Alert Banner - Full details below hero */}
      {kevEntry && (
        <div className="mb-6">
          <KEVBadge kevEntry={kevEntry} variant="full" />
        </div>
      )}

      {/* Severity Explanation - Collapsible */}
      {score !== undefined && severity && (
        <div className="mb-6">
          <SeverityExplainer score={score} severity={severity} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 min-h-[44px] font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === tab.id
                ? 'border-blue-700 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Vulnerability Summary</h2>

            {cve.cvssV3 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">CVSS v3 Score</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-950 dark:text-white">{cve.cvssV3.baseScore}</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(cve.cvssV3.baseSeverity)}`}>
                    {cve.cvssV3.baseSeverity}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 font-mono">
                  {cve.cvssV3.vectorString}
                </p>
              </div>
            )}

            {cve.cvssV2 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">CVSS v2 Score</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-950 dark:text-white">{cve.cvssV2.baseScore}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 font-mono">
                  {cve.cvssV2.vectorString}
                </p>
              </div>
            )}

            {cve.epss && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">EPSS Score (Exploitation Probability)</h3>
                <EPSSBadge epss={cve.epss} variant="full" />
              </div>
            )}

            {cve.cwe && cve.cwe.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">CWE Classification</h3>
                <div className="flex flex-wrap gap-2">
                  {cve.cwe.map((cweId) => (
                    isValidCWEId(cweId) ? (
                      <Link
                        key={cweId}
                        href={`/tools/security/cwe-lookup/${cweId.toLowerCase()}`}
                        className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        {cweId}
                      </Link>
                    ) : (
                      <span
                        key={cweId}
                        className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                        title="This is a placeholder CWE ID indicating the weakness has not been mapped to a specific CWE"
                      >
                        {cweId}
                      </span>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'cvss' && cve.cvssV3 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">CVSS v3 Metrics</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Attack Vector</span>
                <p className="font-medium">{cve.cvssV3.attackVector}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Attack Complexity</span>
                <p className="font-medium">{cve.cvssV3.attackComplexity}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Privileges Required</span>
                <p className="font-medium">{cve.cvssV3.privilegesRequired}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">User Interaction</span>
                <p className="font-medium">{cve.cvssV3.userInteraction}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Scope</span>
                <p className="font-medium">{cve.cvssV3.scope}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Confidentiality Impact</span>
                <p className="font-medium">{cve.cvssV3.confidentialityImpact}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Integrity Impact</span>
                <p className="font-medium">{cve.cvssV3.integrityImpact}</p>
              </div>
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Availability Impact</span>
                <p className="font-medium">{cve.cvssV3.availabilityImpact}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'epss' && cve.epss && (
          <div className="space-y-6">
            {/* EPSS Explanation */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                EPSS (Exploit Prediction Scoring System)
              </h2>
              <EPSSBadge epss={cve.epss} variant="full" />

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">What is EPSS?</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                  EPSS is a data-driven model developed by FIRST.org that estimates the probability
                  of a vulnerability being exploited in the wild within the next 30 days. Unlike CVSS
                  which measures theoretical severity, EPSS predicts real-world exploitation activity.
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li><strong>{'>'} 70%:</strong> High probability - prioritize immediate remediation</li>
                  <li><strong>30-70%:</strong> Medium probability - schedule remediation soon</li>
                  <li><strong>{'<'} 30%:</strong> Lower probability - routine patching schedule</li>
                </ul>
              </div>
            </div>

            {/* Risk Matrix - Only show if we have CVSS score */}
            {score !== undefined && (
              <RiskMatrix cvssScore={score} epssScore={cve.epss.score} />
            )}

            {/* Comparison with KEV */}
            {kevEntry && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Confirmed Active Exploitation
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  This vulnerability is in the CISA Known Exploited Vulnerabilities (KEV) catalog,
                  confirming it has been actively exploited in real-world attacks. The EPSS score
                  provides additional predictive data for ongoing exploitation risk.
                </p>
                <KEVBadge kevEntry={kevEntry} variant="compact" />
              </div>
            )}
          </div>
        )}

        {activeTab === 'references' && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">References ({cve.references.length})</h2>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {cve.references.map((ref, idx) => (
                <div key={idx} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0">
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 break-all"
                  >
                    {ref.url}
                  </a>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{ref.source}</span>
                    {ref.tags && ref.tags.map((tag, tagIdx) => (
                      <span
                        key={tagIdx}
                        className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'remediation' && (
          <div className="space-y-6">
            {/* CISA KEV Information */}
            {kevEntry && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-500" />
                  CISA KEV Alert
                </h2>
                <KEVBadge kevEntry={kevEntry} variant="full" />
              </div>
            )}

            {/* CWE-Based Mitigation Strategies */}
            {cve.cweMitigations && cve.cweMitigations.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-2">CWE-Based Mitigation Strategies</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  General mitigation strategies from MITRE CWE database for the underlying weakness types associated with this vulnerability.
                </p>

                <div className="space-y-6">
                  {cve.cweMitigations.map((cweMitigation, cweIdx) => (
                    <div key={cweIdx}>
                      <div className="flex items-center gap-2 mb-3">
                        <Link
                          href={`/tools/security/cwe-lookup/${cweMitigation.cweId.toLowerCase()}`}
                          className="text-lg font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {cweMitigation.cweId}: {cweMitigation.cweName}
                        </Link>
                      </div>

                      <div className="space-y-4">
                        {cweMitigation.mitigations.map((mitigation, mitIdx) => (
                          <div
                            key={mitIdx}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50"
                          >
                            <div className="mb-3">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Phase</h4>
                              <div className="flex flex-wrap gap-1">
                                {mitigation.phase.map((p) => (
                                  <span key={p} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium">
                                    {p}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="mb-3">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Description</h4>
                              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{mitigation.description}</p>
                            </div>
                            {mitigation.strategy && (
                              <div className="mb-3">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Strategy</h4>
                                <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded text-xs font-medium">
                                  {mitigation.strategy}
                                </span>
                              </div>
                            )}
                            {mitigation.effectiveness && (
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Effectiveness</h4>
                                <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded text-xs font-medium">
                                  {mitigation.effectiveness}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 text-right">
                        <Link
                          href={`/tools/security/cwe-lookup/${cweMitigation.cweId.toLowerCase()}`}
                          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                        >
                          View full {cweMitigation.cweId} details
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vendor/Patch Remediation Links */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-2">Vendor Patches and Advisories</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Official patches, security advisories, and vendor-specific remediation guidance.
              </p>

              {cve.remediation && cve.remediation.length > 0 ? (
                <div className="space-y-3">
                  {cve.remediation.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors break-all"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  No specific vendor patches or advisories tagged. Check the References tab for additional information.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Related CVEs Section */}
      {(relatedByCWE.length > 0 || relatedBySeverity.length > 0) && (
        <RelatedCVEs
          currentCveId={cve.id}
          relatedByCWE={relatedByCWE}
          relatedBySeverity={relatedBySeverity}
          cweIds={cve.cwe}
          severity={severity}
        />
      )}

      {/* Related Resources Section */}
      <div className="mt-8">
        <RelatedResources vectorString={vectorString} severity={severity} />
      </div>
    </div>
  );
}
